import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Stack,
    Text,
    Button,
    Drawer,
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
    TextField,
    Dropdown,
    Alert,
    Actions,
    Columns,
    Column,
    Divider,
    Dialog,
    Checkbox,
    Badge
} from 'braid-design-system';
import { useToast } from '../../hooks/useToast';
import {
    getStandingOrdersByAccount,
    bulkCreateStandingOrders,
    StandingOrderRecord,
    StandingOrderPreview,
    getStandingOrderCustomerSavingAccounts
} from '../../services/standingOrderService';
import { getCustomerByNumber } from '../../services/customerService';
import { Schemas } from '../../types/api-types';
import CustomerSelectionSection from '../CustomerSelectionSection';
import DateField from '../DateField';

interface StandingOrderDrawerProps {
    open: boolean;
    onClose: () => void;
    fromAccount: {
        id: number;
        ref_account_number: string;
        customer: {
            id: number;
            ref_number: string;
            name_ln1: string;
        };
    };
}

type CustomerWithAccounts = Schemas['CustomerResponse'] & {
    accounts?: Schemas['PlAccountResponse'][];
};

type AccountWithCustomer = Schemas['PlAccountResponse'] & {
    customer?: {
        name_ln1?: string | null;
    };
    account_type?: {
        name_ln1?: string | null;
    };
};

const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString();
};

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

interface GroupedStandingOrders {
    from_account_number: string;
    to_account_number: string;
    to_customer_name: string;
    amount: number;
    records: StandingOrderRecord[];
}

const groupStandingOrders = (orders: StandingOrderRecord[]): GroupedStandingOrders[] => {
    const groups: { [key: string]: GroupedStandingOrders } = {};

    orders.forEach((order) => {
        const fromAcc = order.from_account_number || 'N/A';
        const toAcc = order.to_account_number || 'N/A';
        const amt = order.amount || 0;
        const key = `${fromAcc}_${toAcc}_${amt}`;

        if (!groups[key]) {
            groups[key] = {
                from_account_number: fromAcc,
                to_account_number: toAcc,
                to_customer_name: order.to_customer_name || 'No Name',
                amount: amt,
                records: []
            };
        }
        groups[key].records.push(order);
    });

    return Object.values(groups);
};

const StandingOrderDrawer: React.FC<StandingOrderDrawerProps> = ({
    open,
    onClose,
    fromAccount
}) => {
    const toast = useToast();
    const inputRef = useRef<HTMLInputElement>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [standingOrders, setStandingOrders] = useState<StandingOrderRecord[]>([]);
    const [loading, setLoading] = useState(false);

    // Form state
    const [isSameCustomer, setIsSameCustomer] = useState(false);
    const [toCustomerNumber, setToCustomerNumber] = useState('');
    const [toCustomerInfo, setToCustomerInfo] = useState<CustomerWithAccounts | null>(null);
    const [toAccountId, setToAccountId] = useState<number | null>(null);
    const [toAccounts, setToAccounts] = useState<AccountWithCustomer[]>([]);
    const [fromDate, setFromDate] = useState('');
    const [period, setPeriod] = useState('');
    const [amount, setAmount] = useState('');
    const [previewRecords, setPreviewRecords] = useState<StandingOrderPreview[]>([]);
    const [showPreview, setShowPreview] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

    const toggleGroup = (key: string) => {
        setExpandedGroups((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    useEffect(() => {
        if (open && fromAccount?.id && fromAccount.id !== 0) {
            loadStandingOrders();
        }
    }, [open, fromAccount?.id]);

    const loadStandingOrders = async () => {
        if (!fromAccount?.id || fromAccount.id === 0) return;
        setLoading(true);
        try {
            const data = await getStandingOrdersByAccount(fromAccount.id);
            setStandingOrders(data);
        } catch (error: unknown) {
            toast.showError(error instanceof Error ? error.message : 'Failed to load standing orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchSavingAccountsForCustomer = async (customerId: number) => {
        try {
            const savingAccounts = await getStandingOrderCustomerSavingAccounts(customerId);
            setToAccounts(savingAccounts as AccountWithCustomer[]);
            if (savingAccounts.length > 0 && savingAccounts[0]?.id) {
                setToAccountId(savingAccounts[0].id);
            } else {
                setToAccountId(null);
            }
        } catch (error: unknown) {
            toast.showError(error instanceof Error ? error.message : 'Failed to fetch customer saving accounts');
            setToAccounts([]);
            setToAccountId(null);
        }
    };

    const fetchCustomerInfo = async () => {
        if (!toCustomerNumber) return;
        try {
            const customer = await getCustomerByNumber(toCustomerNumber);
            const customerWithAccounts = customer as CustomerWithAccounts;
            setToCustomerInfo(customerWithAccounts || null);

            if (customerWithAccounts && customerWithAccounts.id) {
                await fetchSavingAccountsForCustomer(customerWithAccounts.id);
            } else {
                setToAccounts([]);
                setToAccountId(null);
            }
        } catch (error: unknown) {
            toast.showError(error instanceof Error ? error.message : 'Failed to fetch customer');
            setToCustomerInfo(null);
            setToAccounts([]);
            setToAccountId(null);
        }
    };

    const handlePreview = () => {
        setValidationError('');

        // Validation
        if (!toAccountId) {
            setValidationError('Please select a to account');
            return;
        }
        if (!fromDate) {
            setValidationError('Please select from date');
            return;
        }
        const parsedPeriod = parseInt(period);
        if (Number.isNaN(parsedPeriod) || parsedPeriod <= 0) {
            setValidationError('Period must be greater than 0');
            return;
        }
        const parsedAmount = parseFloat(amount);
        if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
            setValidationError('Amount must be greater than 0');
            return;
        }

        const selectedToAccount = toAccounts.find((acc) => acc.id === toAccountId);

        // Generate N monthly records starting from fromDate on client-side
        const records: StandingOrderPreview[] = [];
        const start = new Date(fromDate);
        const startYear = start.getFullYear();
        const startMonth = start.getMonth();
        const startDay = start.getDate();

        for (let i = 0; i < parsedPeriod; i++) {
            let targetYear = startYear;
            let targetMonth = startMonth + i;

            // Adjust year and month
            targetYear += Math.floor(targetMonth / 12);
            targetMonth = targetMonth % 12;

            // Find maximum days in target month
            const maxDays = new Date(targetYear, targetMonth + 1, 0).getDate();
            const targetDay = Math.min(startDay, maxDays);

            const processDateObj = new Date(targetYear, targetMonth, targetDay);
            // Format as YYYY-MM-DD
            const yearStr = processDateObj.getFullYear();
            const monthStr = String(processDateObj.getMonth() + 1).padStart(2, '0');
            const dayStr = String(processDateObj.getDate()).padStart(2, '0');
            const processDate = `${yearStr}-${monthStr}-${dayStr}`;

            records.push({
                process_date: processDate,
                from_account_id: fromAccount.id,
                to_account_id: toAccountId,
                amount: parsedAmount,
                from_account_number: fromAccount.ref_account_number || null,
                to_account_number: selectedToAccount?.ref_account_number ?? null,
                from_account_name: fromAccount.customer.name_ln1 || null,
                to_account_name: selectedToAccount?.account_type?.name_ln1 ?? 'Saving Account'
            });
        }

        setPreviewRecords(records);
        setShowPreview(true);
    };

    const handleCreate = async () => {
        if (previewRecords.length === 0) return;
        setSubmitting(true);
        const requestPayload = {
            records: previewRecords.map((r) => ({
                from_account_id: r.from_account_id,
                to_account_id: r.to_account_id,
                amount: r.amount,
                process_date: r.process_date
            }))
        };

        try {
            const response = await bulkCreateStandingOrders(requestPayload);
            toast.showSuccess('Success', `Created ${response.records_created} standing order records`);
            setShowCreateForm(false);
            setShowPreview(false);
            setPreviewRecords([]);
            setIsSameCustomer(false);
            setToCustomerNumber('');
            setToCustomerInfo(null);
            setToAccountId(null);
            setToAccounts([]);
            setFromDate('');
            setPeriod('');
            setAmount('');
            setValidationError('');
            setExpandedGroups([]);
            loadStandingOrders();
        } catch (error: unknown) {
            setValidationError(error instanceof Error ? error.message : 'Failed to create standing orders');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelCreate = () => {
        setShowCreateForm(false);
        setShowPreview(false);
        setPreviewRecords([]);
        setIsSameCustomer(false);
        setToCustomerNumber('');
        setToCustomerInfo(null);
        setToAccountId(null);
        setToAccounts([]);
        setFromDate('');
        setPeriod('');
        setAmount('');
        setValidationError('');
        setExpandedGroups([]);
    };


    return (
        <Drawer
            title="Standing Orders"
            open={open}
            onClose={onClose}
            width="large"
        >
            <Stack space="medium">
                {showCreateForm ? (
                    <Stack space="medium">
                        {/* From Account Info (Read-only) */}
                        <Box background="neutralLight" padding="medium" borderRadius="small">
                            <Stack space="small">
                                <Text weight="strong">From Account</Text>
                                <Text>Account Number: {fromAccount.ref_account_number}</Text>
                                <Text>Member Number: {fromAccount.customer.ref_number}</Text>
                                <Text>Member Name: {fromAccount.customer.name_ln1}</Text>
                            </Stack>
                        </Box>

                        {/* Transfer to Same Customer Checkbox */}
                        <Box>
                            <Checkbox
                                checked={isSameCustomer}
                                onChange={() => {
                                    const nextVal = !isSameCustomer;
                                    setIsSameCustomer(nextVal);
                                    if (nextVal) {
                                        setToCustomerNumber(fromAccount.customer.ref_number);
                                        fetchSavingAccountsForCustomer(fromAccount.customer.id);
                                    } else {
                                        setToCustomerNumber('');
                                        setToCustomerInfo(null);
                                        setToAccounts([]);
                                        setToAccountId(null);
                                    }
                                }}
                                label="Transfer to Same Customer"
                            />
                        </Box>

                        {/* To Customer Selection */}
                        {!isSameCustomer && (
                            <CustomerSelectionSection
                                customerNumber={toCustomerNumber}
                                setCustomerNumber={setToCustomerNumber}
                                inputRef={inputRef}
                                fetchCustomerInfo={fetchCustomerInfo}
                                customerInfo={toCustomerInfo}
                                handleCusomerClick={() => { }}
                                feildDisable={false}
                                hideAddButton={true}
                                hideActionColumn={true}
                                showChequeBooks={false}
                                showNotes={false}
                                showExtras={false}
                            />
                        )}

                        {/* To Account Dropdown & Select Date in Same Columns */}
                        <Columns space="large" collapseBelow="tablet">
                            <Column width="1/2">
                                <Dropdown
                                    id="toAccountSelect"
                                    value={toAccountId ? String(toAccountId) : ""}
                                    onChange={(e) => {
                                        const v = e.currentTarget.value;
                                        setToAccountId(v ? Number(v) : null);
                                    }}
                                    placeholder="Select To Account"
                                    label="Select To Account"
                                    children={toAccounts.map((acc) => (
                                        <option key={String(acc.id)} value={String(acc.id)}>
                                            {acc.ref_account_number} - {acc.account_type?.name_ln1 || 'Saving Account'}
                                        </option>
                                    )) as any}
                                />
                            </Column>
                            <Column width="1/2">
                                <DateField
                                    value={fromDate}
                                    onlyForToday={false}
                                    onChange={(value) => setFromDate(value)}
                                    label="From Date"
                                />
                            </Column>
                        </Columns>

                        {/* Period and Amount */}
                        <Columns space="large" collapseBelow="tablet">
                            <Column width="1/2">
                                <TextField
                                    type="number"
                                    value={period}
                                    onChange={(e) => setPeriod(e.currentTarget.value)}
                                    label="Period (Months)"
                                    placeholder="Enter number of months"
                                />
                            </Column>
                            <Column width="1/2">
                                <TextField
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.currentTarget.value)}
                                    label="Amount"
                                    placeholder="Enter amount"
                                />
                            </Column>
                        </Columns>

                        {validationError ? (
                            <Alert tone="critical">
                                <Text>{validationError}</Text>
                            </Alert>
                        ) : null}

                        <Actions>
                            <Button onClick={handlePreview}>
                                Preview
                            </Button>
                            <Button variant="ghost" onClick={handleCancelCreate}>
                                Cancel
                            </Button>
                        </Actions>
                    </Stack>
                ) : (
                    <Box display="flex" justifyContent="flexEnd">
                        <Actions>
                            <Button onClick={() => setShowCreateForm(true)}>
                                Create New Standing Order
                            </Button>
                        </Actions>
                    </Box>
                )}

                {/* Dialog Preview Modal */}
                <Dialog
                    id="standing-order-preview-dialog"
                    title="Standing Order Schedule Preview"
                    open={showPreview}
                    onClose={() => setShowPreview(false)}
                    width="medium"
                >
                    <Stack space="medium">
                        <Table label="Preview Records">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Process Date</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">From Account</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">To Account</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Amount</Text>
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {previewRecords.map((record, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Text size="small">{formatDate(record.process_date)}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text size="small">{record.from_account_number ?? '-'}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Text size="small">{record.to_account_number ?? '-'}</Text>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Text size="small">{formatCurrency(record.amount)}</Text>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Actions>
                            <Button onClick={handleCreate} loading={submitting}>
                                Confirm & Create
                            </Button>
                            <Button variant="ghost" onClick={() => setShowPreview(false)}>
                                Cancel
                            </Button>
                        </Actions>
                    </Stack>
                </Dialog>

                <Divider />

                <Stack space="medium">
                    <Text weight="strong" size="large">Existing Standing Orders</Text>
                    {loading ? (
                        <Text tone="secondary">Loading standing orders...</Text>
                    ) : standingOrders.length === 0 ? (
                        <Text tone="secondary">No standing orders found for this account.</Text>
                    ) : (
                        <Table label="Standing Orders">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        <Text size="xsmall">From Account</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">To Account</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">To Customer</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Amount</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Date</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Status</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Action</Text>
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {groupStandingOrders(standingOrders).map((group, groupIdx) => {
                                    const groupKey = `${group.from_account_number}_${group.to_account_number}_${group.amount}`;
                                    const isExpanded = expandedGroups.includes(groupKey);
                                    return (
                                        <React.Fragment key={groupIdx}>
                                            {/* Group Header Row */}
                                            <TableRow>
                                                <TableCell>
                                                    <Text size="small" weight="strong">{group.from_account_number}</Text>
                                                </TableCell>
                                                <TableCell>
                                                    <Text size="small" weight="strong">{group.to_account_number}</Text>
                                                </TableCell>
                                                <TableCell>
                                                    <Text size="small" weight="strong">{group.to_customer_name}</Text>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Text size="small" weight="strong">{formatCurrency(group.amount)}</Text>
                                                </TableCell>
                                                <TableCell>
                                                    <Text size="small" tone="secondary"></Text>
                                                </TableCell>
                                                <TableCell>
                                                    <Text size="small" tone="secondary"></Text>
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="ghost"
                                                        tone="brandAccent"
                                                        size="small"
                                                        onClick={() => toggleGroup(groupKey)}
                                                    >
                                                        {isExpanded ? 'Hide' : 'Details'}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                            {/* Group Detail Rows */}
                                            {isExpanded && group.records.map((record) => (
                                                <TableRow key={record.id}>
                                                    <TableCell>
                                                        <Text size="small" tone="secondary"></Text>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Text size="small" tone="secondary"></Text>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Text size="small" tone="secondary"></Text>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Text size="small" tone="secondary"></Text>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Text size="small">{formatDate(record.process_date)}</Text>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge tone={record.status === 0 ? 'caution' : 'positive'}>
                                                            {record.status === 0 ? 'Pending' : 'Completed'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Text size="small" tone="secondary"></Text>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </Stack>
            </Stack>
        </Drawer>
    );
};

export default StandingOrderDrawer;
